// Subscription tier, read from the account rather than the token.
//
// This is now a thin read of SubscriptionProvider, which holds one live state
// for the whole app and refreshes it on sign-in, on window focus, and after a
// billing action. The hook keeps its original shape so existing callers did
// not have to change.
//
// What it replaced: a module-level 30-second cache that nothing invalidated
// when the subscription actually changed. Subscribing, upgrading or
// cancelling left every mounted component showing the old plan until the TTL
// happened to lapse — and components that had already read it kept their
// stale copy indefinitely.
//
// Still true, and the reason this exists at all: useAuthPermissions answers
// "is this user premium" from user_metadata.role, a claim baked into the JWT
// at sign-in. It does not change when someone subscribes, upgrades or
// cancels; it only refreshes at the next login. Anything gating on it will
// leave a paying customer behind a paywall. Gate on this instead.

import { SubscriptionTier } from '../../services/Billing/billingService';
import { useSubscriptionContext } from '../../providers/SubscriptionProvider';

export interface SubscriptionTierInfo {
  /** The active tier, or null when there is no subscription. */
  tier: SubscriptionTier | null;
  isPremium: boolean;
  isPremiumPlus: boolean;
  /** True when the tier is at least `required` — Plus satisfies 'premium'. */
  hasTier: (required: SubscriptionTier) => boolean;
  /** A cancellation is scheduled but the paid period is still running. */
  cancelAtPeriodEnd: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useSubscriptionTier(): SubscriptionTierInfo {
  const { tier, isPremium, isPremiumPlus, hasTier, cancelAtPeriodEnd, loading, refresh } =
    useSubscriptionContext();

  return { tier, isPremium, isPremiumPlus, hasTier, cancelAtPeriodEnd, loading, refresh };
}
